/**
 * Temp Alert 메시지 다국어 지원
 *
 * 사용법:
 * import { getTempAlertMessage } from '@src/shared/constants/tempAlertMessages';
 * const message = getTempAlertMessage('UPDATED_SUCCESSFULLY');
 * const messageWithParams = getTempAlertMessage('WELCOME', { name: 'John' });
 */

import type { LocalizedMessages } from '../dto/EntityRepository';
import { useLanguageStore, Language } from '../store/languageStore';

export type { Language } from '../store/languageStore';

export type TempAlertMessageKey =
  // tempAlertStore preset
  | 'UPDATED_SUCCESSFULLY'
  | 'DELETED_SUCCESSFULLY'
  | 'UNAUTHORIZED_ACCESS'
  | 'SUBSCRIPTION_SUCCESS'
  // useAuth
  | 'WELCOME'
  | 'VERIFICATION_CODE_SENT'
  | 'VERIFY_EMAIL_PASSWORD_RESET'
  | 'SENT_TO_EMAIL_CHECK_INBOX'
  | 'GOODBYE'
  | 'VERIFY_YOUR_EMAIL'
  // usePayment
  | 'REDIRECTING_POLAR_SUBSCRIPTION'
  | 'THANK_YOU_INTEREST'
  // usePasswordCheck
  | 'PASSWORD_NOT_SET'
  // useSupport
  | 'MESSAGE_SENT_THANKS'
  // Error page
  | 'ERROR_INVALID_ARTIST_ID'
  | 'ERROR_ARTIST_ID_FORMAT'
  | 'ERROR_REDIRECTING_HOME';

const tempAlertMessages: LocalizedMessages<TempAlertMessageKey> = {
  UPDATED_SUCCESSFULLY: {
    [Language.EN]: "Updated successfully.",
    [Language.KR]: "저장되었습니다.",
  },
  DELETED_SUCCESSFULLY: {
    [Language.EN]: "Deleted successfully.",
    [Language.KR]: "항목이 삭제되었습니다.",
  },
  UNAUTHORIZED_ACCESS: {
    [Language.EN]: "Unauthorized access.",
    [Language.KR]: "접근 권한이 없습니다.",
  },
  SUBSCRIPTION_SUCCESS: {
    [Language.EN]: "Subscription successful. Welcome aboard! 🎉",
    [Language.KR]: "구독이 완료되었습니다. 환영합니다! 🎉",
  },
  WELCOME: {
    [Language.EN]: "Welcome.",
    [Language.KR]: "로그인 완료.",
  },
  VERIFICATION_CODE_SENT: {
    [Language.EN]: "Verification code sent. Check your email.",
    [Language.KR]: "인증 코드가 발송되었습니다. 이메일을 확인해주세요.",
  },
  VERIFY_EMAIL_PASSWORD_RESET: {
    [Language.EN]: "Please verify your email for password reset.",
    [Language.KR]: "비밀번호 재설정을 위해 이메일을 인증해주세요.",
  },
  SENT_TO_EMAIL_CHECK_INBOX: {
    [Language.EN]: "We've sent it to your email. Check your inbox.",
    [Language.KR]: "이메일로 발송했습니다. 받은편지함을 확인해주세요.",
  },
  GOODBYE: {
    [Language.EN]: "Goodbye.",
    [Language.KR]: "로그아웃 완료.",
  },
  VERIFY_YOUR_EMAIL: {
    [Language.EN]: "Please verify your email.",
    [Language.KR]: "이메일을 인증해주세요.",
  },
  REDIRECTING_POLAR_SUBSCRIPTION: {
    [Language.EN]: "Redirecting to Polar.sh portal for subscription",
    [Language.KR]: "Polar.sh 구독 페이지로 이동 중입니다",
  },
  THANK_YOU_INTEREST: {
    [Language.EN]: "Thank you for your interest.",
    [Language.KR]: "관심에 감사드립니다.",
  },
  PASSWORD_NOT_SET: {
    [Language.EN]: "Password not set. Set it now. ->",
    [Language.KR]: "비밀번호를 먼저 설정해주세요.",
  },
  MESSAGE_SENT_THANKS: {
    [Language.EN]: "Thanks for reaching out.",
    [Language.KR]: "문의가 접수되었습니다.",
  },
  // Error page
  ERROR_INVALID_ARTIST_ID: {
    [Language.EN]: "Error: Invalid artist ID or username",
    [Language.KR]: "Link ID가 유효하지 않습니다.",
  },
  ERROR_ARTIST_ID_FORMAT: {
    [Language.EN]: "The provided artist ID or username does not match the required format.",
    [Language.KR]: "다시 한번 확인해주세요.",
  },
  ERROR_REDIRECTING_HOME: {
    [Language.EN]: "Redirecting to home in {countdown} seconds.",
    [Language.KR]: "{countdown}초 후 Home으로 이동합니다.",
  },
};

/**
 * Temp Alert 메시지를 가져오는 함수
 * 언어는 languageStore에서 자동으로 가져옵니다.
 * @param key 메시지 키
 * @param params 메시지에 포함될 파라미터 (예: {name: 'John'})
 * @returns 번역된 메시지
 */
export function getTempAlertMessage(
  key: TempAlertMessageKey,
  params?: Record<string, string | number>
): string {
  const language = useLanguageStore.getState().language;
  const message = tempAlertMessages[key];

  if (!message) {
    console.warn(`Temp alert message key "${key}" not found.`);
    return key;
  }

  let translatedMessage = message[language] || message[Language.EN];

  if (params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      translatedMessage = translatedMessage.replace(
        new RegExp(`\\{${paramKey}\\}`, 'g'),
        String(paramValue)
      );
    });
  }

  return translatedMessage;
}
